import { useId, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@monksflow/monks-ui';
import { XIcon } from 'lucide-react';
import { BYTES_PER_MEGABYTE, fileAcceptAttribute, fileTypeDisplayNames } from '@/entities/brief';
import { validateFiles, type FileFieldConfig } from './validateFiles';
import { ERROR_SLOT_PROPS } from './errorSlot';
import { attachmentTypeLabel } from './attachmentTypeLabel';
import type { ResolvedAttachment } from './useAttachmentResolver';

export interface FileFieldValue {
  assetIds: string[];
}

interface FileFieldProps {
  value: FileFieldValue | null;
  config: FileFieldConfig;
  disabled?: boolean;
  /** An upload is in flight — the control says so and stops accepting a second batch. */
  isUploading?: boolean;
  onChange: (next: FileFieldValue) => void;
  /**
   * Files that passed client-side validation (mime/size/count). The caller owns the actual
   * upload to assets-api and appends the resulting asset ids via `onChange` once it resolves —
   * this widget never calls the network itself, keeping it pure and controlled.
   */
  onFilesSelected?: (files: File[]) => void;
  /**
   * Resolves an attachment's display metadata for rendering a chip/thumbnail. Optional so the
   * widget still renders (id-only) before the caller has hydrated asset metadata.
   */
  resolveAsset?: (assetId: string) => ResolvedAttachment | undefined;
}

/** Rounded up, so a file one byte over the limit never reads as the limit itself. */
function toMegabytes(bytes: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
    Math.ceil((bytes / BYTES_PER_MEGABYTE) * 10) / 10,
  );
}

/**
 * Pure, controlled. Validates candidate files against `config` before ever calling
 * `onFilesSelected` — a disallowed mime type, an oversized file, or exceeding `maxCount` is
 * rejected here and surfaced in the error slot, never handed to the caller to upload.
 */
export function FileField({
  value,
  config,
  disabled = false,
  isUploading = false,
  onChange,
  onFilesSelected,
  resolveAsset,
}: FileFieldProps) {
  const { t, i18n } = useTranslation('projects');
  const inputId = useId();
  const captionId = useId();
  const assetIds = value?.assetIds ?? [];
  const isLocked = disabled || isUploading;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const typeNames = config.acceptedMimeTypes
    ? fileTypeDisplayNames(config.acceptedMimeTypes).join(', ')
    : null;
  const sizeLimit =
    config.maxSizeBytes != null ? toMegabytes(config.maxSizeBytes, i18n.language) : null;
  const caption =
    typeNames && sizeLimit
      ? t('projectBriefDetail.briefSectionField.fileField.limits', {
          types: typeNames,
          size: sizeLimit,
        })
      : sizeLimit
        ? t('projectBriefDetail.briefSectionField.fileField.sizeLimit', { size: sizeLimit })
        : typeNames;

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;

    const { accepted, rejected } = validateFiles(files, assetIds, config);
    if (rejected.length > 0) {
      const first = rejected[0];
      const name = first.file.name;
      setErrorMessage(
        first.reason === 'type'
          ? t('projectBriefDetail.briefSectionField.fileField.rejectedType', {
              name,
              types: typeNames ?? '',
            })
          : first.reason === 'size'
            ? t('projectBriefDetail.briefSectionField.fileField.rejectedSize', {
                name,
                size: toMegabytes(first.file.size, i18n.language),
                limit: sizeLimit ?? '',
              })
            : t('projectBriefDetail.briefSectionField.fileField.rejectedCount', {
                max: config.maxCount ?? 0,
              }),
      );
    } else {
      setErrorMessage(null);
    }

    if (accepted.length > 0) {
      onFilesSelected?.(accepted);
    }
  };

  const handleRemove = (assetId: string) => {
    onChange({ assetIds: assetIds.filter((id) => id !== assetId) });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {assetIds.map((assetId) => {
          const asset = resolveAsset?.(assetId);
          // Falling back to the id keeps an attachment whose metadata will not resolve visible
          // and removable, rather than stranding the reference on an invisible chip.
          const name = asset?.name ?? assetId;
          const typeLabel = asset ? attachmentTypeLabel(asset.name, asset.mimeType) : null;
          const thumbnailUrl = asset?.mimeType.startsWith('image/') ? asset.url : undefined;
          return (
            <div
              key={assetId}
              className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1"
            >
              {thumbnailUrl && (
                <img src={thumbnailUrl} alt={name} className="size-6 rounded object-cover" />
              )}
              <span className="text-sm">{name}</span>
              {typeLabel && (
                <span data-testid="brief-attachment-type" className="text-xs text-muted-foreground">
                  {typeLabel}
                </span>
              )}
              <button
                type="button"
                aria-label={t('projectBriefDetail.briefSectionField.fileField.remove', { name })}
                disabled={isLocked}
                onClick={() => handleRemove(assetId)}
              >
                <XIcon className="size-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-xs">
        <Button asChild variant="outline" size="sm" disabled={isLocked} className="w-fit">
          <label htmlFor={inputId}>
            {t('projectBriefDetail.briefSectionField.fileField.addFile')}
          </label>
        </Button>
        {isUploading && (
          <Spinner
            className="text-muted-foreground"
            aria-label={t('projectBriefDetail.briefSectionField.fileField.uploading')}
          />
        )}
      </div>
      {caption && (
        <p id={captionId} className="text-xs text-muted-foreground">
          {caption}
        </p>
      )}
      <input
        id={inputId}
        type="file"
        multiple
        accept={
          config.acceptedMimeTypes ? fileAcceptAttribute(config.acceptedMimeTypes) : undefined
        }
        aria-describedby={caption ? captionId : undefined}
        disabled={isLocked}
        aria-label={t('projectBriefDetail.briefSectionField.fileField.addFile')}
        className="sr-only"
        onChange={handleFileInputChange}
      />

      <div {...ERROR_SLOT_PROPS} className="text-xs text-destructive">
        {errorMessage}
      </div>
    </div>
  );
}
